/**
 * LA LISTA DE LA BANDEJA: UNA PÁGINA VIVA Y UNA COLA CONGELADA.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR QUÉ HAY DOS MITADES (24-sep-2026)
 * ───────────────────────────────────────────────────────────────────────────────
 * El oyente cubre la PÁGINA 1 —`orderBy('actualizadoEn','desc')`, `limit(200)`— y las
 * siguientes se traen con `getDocs`, congeladas. Ampliar el oyente a todas obligaría a
 * reengancharlo cada vez que se toca un filtro, y cada reenganche cuesta la página entera.
 *
 * La consecuencia es que **el mismo chat puede existir en las dos mitades**, y ahí está
 * todo el trabajo de este módulo.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * EL CASO QUE OBLIGA A FUSIONAR, Y SU SÍNTOMA INDESCIFRABLE
 * ───────────────────────────────────────────────────────────────────────────────
 * Un chat vive en la página 2 (congelada). Llega un mensaje → sube a la posición 1 →
 * entra en el rango del oyente. Ahora hay dos copias del mismo id: la vieja congelada y
 * la nueva viva.
 *
 * Sin fusión, el operador ve **el mismo chat dos veces** — y nadie ataría eso a la
 * paginación: parecería un fallo de datos.
 *
 * ⚠️ La deduplicación que ya existía en `Chats.js` NO servía: es de ANEXADO —protege
 * contra traer dos veces la misma página— y esto es una FUSIÓN entre dos orígenes.
 *
 * **LA COPIA VIVA GANA SIEMPRE, y en los dos sentidos**: cuando el oyente emite y cuando
 * llega una página. No es una convención: la copia del oyente es, por definición, la que
 * Firestore acaba de confirmar.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * ⚠️ UNA SALIDA DEL OYENTE NO BORRA: MUEVE
 * ───────────────────────────────────────────────────────────────────────────────
 * Con `limit(200)` y 296 chats, si uno de los 96 de abajo sube, **el que estaba en la
 * posición 200 cae a la 201** y el oyente emite su salida (`removed`).
 *
 * Ese chat NO está en la cola congelada —se trajo antes de que cayera— así que borrarlo
 * lo haría **desaparecer de la lista sin que nadie lo borrara**: un chat que se esfuma sin
 * causa visible, que es justo el tipo de fallo que nadie sabe reportar.
 *
 * Así que sale de los vivos y **entra en la cola congelada**, que es exactamente donde le
 * corresponde ahora. Cuesta cero lecturas.
 *
 * ───────────────────────────────────────────────────────────────────────────────
 * POR QUÉ ESTO VIVE FUERA DEL PROVEEDOR
 * ───────────────────────────────────────────────────────────────────────────────
 * Mismo motivo que sacó `destinoDelPrivateReply` de `igWebhook`: una fusión probada a
 * través de su llamante acaba teniendo un test que REIMPLEMENTA la fusión, y entonces la
 * mutación que reintroduce el defecto pasa en verde. Aquí el test la llama.
 */

/** Milisegundos de `actualizadoEn`, tolerante a Timestamp, Date, ISO o número. */
export function instanteDeChat(chat) {
  const v = chat?.actualizadoEn;
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const p = Date.parse(v);
  return Number.isFinite(p) ? p : 0;
}

/** El estado inicial: dos mapas vacíos. Mapas y no arrays — la clave es el id. */
export function estadoInicial() {
  return { vivos: new Map(), congelados: new Map() };
}

/**
 * Aplica los cambios de un snapshot del oyente.
 *
 * @param {{vivos:Map, congelados:Map}} estado
 * @param {Array<{type:'added'|'modified'|'removed', id:string, data:object}>} cambios
 * @returns {{vivos:Map, congelados:Map}} estado NUEVO (no muta el recibido)
 */
export function aplicarCambios(estado, cambios) {
  const vivos = new Map(estado.vivos);
  const congelados = new Map(estado.congelados);

  for (const c of cambios || []) {
    if (!c || !c.id) continue;

    if (c.type === 'removed') {
      // MUEVE, no borra. Ver la cabecera: un chat que cae de la página 1 sigue
      // existiendo, sólo que ahora vive más abajo.
      const anterior = vivos.get(c.id);
      if (anterior) {
        vivos.delete(c.id);
        congelados.set(c.id, anterior);
      }
      continue;
    }

    // `added` y `modified` se tratan igual: el oyente manda. Y se retira la copia
    // congelada si la había — ése es el caso del chat que sube desde la página 2.
    vivos.set(c.id, { id: c.id, ...c.data });
    congelados.delete(c.id);
  }

  return { vivos, congelados };
}

/**
 * Anexa una página traída con `getDocs`.
 *
 * ⚠️ Un documento que el oyente YA TIENE se descarta: la página 2 puede devolver un chat
 * que acaba de subir, y la copia de la consulta es más vieja que la del oyente. Misma
 * regla que arriba, aplicada en el otro sentido.
 */
export function anexarPagina(estado, docs) {
  const congelados = new Map(estado.congelados);
  for (const d of docs || []) {
    if (!d || !d.id) continue;
    if (estado.vivos.has(d.id)) continue;   // el vivo gana
    congelados.set(d.id, d);
  }
  return { vivos: estado.vivos, congelados };
}

/**
 * La lista que ve la pantalla: las dos mitades, sin duplicados, en orden.
 *
 * El orden se aplica aquí y no se hereda de Firestore, porque la cola congelada y los
 * vivos vienen de consultas distintas y sólo juntos tienen un orden que signifique algo.
 */
export function listaFusionada(estado) {
  const porId = new Map(estado.congelados);
  for (const [id, chat] of estado.vivos) porId.set(id, chat);
  return [...porId.values()].sort((a, b) => instanteDeChat(b) - instanteDeChat(a));
}

/** Cuántos chats hay en total, sin contar dos veces al duplicado. */
export function totalDeChats(estado) {
  const ids = new Set([...estado.congelados.keys(), ...estado.vivos.keys()]);
  return ids.size;
}
